<template>
  <div>
    <nav-bar />
    <v-container class="mt-10 pt-5">
      <!-- Page Header ########################################### -->
      <header-page
        ref="header"
        titleText="Minhas Doações"
        titleIcon="fal fa-hand-holding-usd"
        :button="{
          text: 'Nova Doação',
          icon: 'fal fa-hand-holding-usd',
          click: () => {
            $router.push({ name: routes.web.NewDonations });
          }
        }"
      />

      <!-- Page Body ########################################### -->
      <v-card :elevation="8">
        <v-container pa-6>
          <v-row justify="space-between">
            <v-col cols="auto">
              <p class="list-labels">Lista de doações</p>
            </v-col>
            <v-skeleton-loader
              ref="skeleton"
              :boilerplate="false"
              type="table-tfoot"
              :tile="false"
              class="ml-auto"
              v-show="loading"
              min-width="300px"
            ></v-skeleton-loader>
            <v-col cols="auto" v-show="!loading">
              <v-container grid-list-md pa-0>
                <v-layout justify-start wrap justify-sm-end>
                  <v-flex col-12 col-sm-6>
                    <v-text-field
                      v-model="currentSearchTerm"
                      clearable
                      label="Buscar"
                      outlined
                      dense
                      hide-details
                      @keyup="addSearchTerm($event)"
                    ></v-text-field>
                  </v-flex>
                  <v-flex flex-grow-0 col-12 col-sm-4>
                    <v-select
                      v-model="selectedOrderBy"
                      :items="orderByOptions"
                      label="Ordenar por"
                      item-text="name"
                      item-value="id"
                      outlined
                      hide-details="auto"
                      dense
                      solo
                      flat
                      @change="loadDonations"
                    ></v-select>
                  </v-flex>
                  <v-flex class="flex-grow-0 order-1 order-sm-3">
                    <v-btn
                      fab
                      text
                      outlined
                      small
                      class="icon-rounded gray ml-0"
                      :color="isFiltered ? 'red' : ''"
                    >
                      <v-icon
                        small
                        :key="`icon-${isFiltered}`"
                        @click="
                          isFiltered = !isFiltered;
                          showFilterDialog = !showFilterDialog;
                        "
                        v-text="isFiltered ? 'fas fa-filter' : 'fal fa-filter'"
                      ></v-icon>
                    </v-btn>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-col>
          </v-row>
        </v-container>

        <!-- Search Results ########################################### -->
        <search-result
          inset
          v-model="searchTerms"
          :totalResults="totalNumberOfDonations"
          @itemRemoved="searchTermRemoved()"
          :showAddButton="false"
        />

        <v-skeleton-loader
          ref="skeleton"
          :boilerplate="false"
          type="table-tbody"
          :tile="false"
          class="mx-auto"
          v-show="loading"
        ></v-skeleton-loader>
        <v-list v-show="!loading">
          <template v-for="(donation, index) in donations">
            <v-divider v-if="index > 0" :key="index"></v-divider>

            <v-list-item
              :key="donation.title"
              class="pa-6 list-group"
              :class="getClassBaseOnDonationStatus(donation.status)"
            >
              <!--canceled, expired, paid-->
              <div class="bullet-dot secondary mr-4"></div>
              <v-list-item-content>
                <v-list-item-action-text>{{
                  donation.donationDate | FormatDate
                }}</v-list-item-action-text>
                <v-list-item-title>
                  <h5 class="primary--text">{{ donation.donationFrom.fullName }}</h5>
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span class="mr-1" v-if="donation.donationTo.name">{{ donation.donationTo.name }}</span>
                  <span class="grey--text text--lighten-1">{{
                    donation.donationFrom.document | FormatDocument
                  }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <div class="ml-4 d-flex text-right justify-center align-center">
                <div class="description-right">
                  {{ donation.paymentMethod | FormatPaymentMethod }}
                  <div>
                    {{
                      donation.status == donationStatus.Paid ||
                      donation.paymentMethod == paymentMethods.BankTransfer
                        ? "Pgto:"
                        : "Venc:"
                    }}
                    <span class="textColor--text">{{
                      (donation.status == donationStatus.Paid ||
                      donation.paymentMethod == paymentMethods.BankTransfer
                        ? donation.paymentDate
                        : donation.dueDate) | FormatDate
                    }}</span>
                  </div>
                </div>
                <div
                  class="slip d-flex ml-4"
                  v-if="
                    donation.status == donationStatus.Pending &&
                      donation.paymentMethod == paymentMethods.BankBillet
                  "
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        size="16px"
                        fab
                        v-on="on"
                        class="secondary icon-rounded icon-background"
                        @click="downloadDonationBillet(donation.billetId)"
                      >
                        <v-icon class="white--text">fal fa-file-alt</v-icon>
                      </v-btn>
                    </template>
                    <span>Segunda via</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        outlined
                        fab
                        size="16px"
                        v-on="on"
                        class="gray icon-rounded hover-red"
                        @click="
                          selectedDonation = donation;
                          showCancelBilletDialog = true;
                        "
                      >
                        <v-icon>fal fa-ban</v-icon>
                      </v-btn>
                    </template>
                    <span>Cancelar Boleto</span>
                  </v-tooltip>
                </div>
                <v-divider class="mx-4" vertical></v-divider>
                <div>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-chip small v-on="on">{{
                        donation.status | FormatDonationStatus
                      }}</v-chip>
                    </template>
                    <span>Status do boleto</span>
                  </v-tooltip>
                </div>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <div class="description-value" v-on="on">
                      {{ donation.donationValue | FormatMoney }}
                    </div>
                  </template>
                  <span>Valor do boleto</span>
                </v-tooltip>
                
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      icon
                      outlined
                      fab
                      size="16px"
                      class="gray icon-rounded ml-7"
                      :class="{ anonymous: donation.isAnonymous }"
                    >
                      <v-icon
                        >fal
                        {{
                          donation.isAnonymous ? "fa-user-slash" : "fa-user"
                        }}</v-icon
                      >
                    </v-btn>
                  </template>
                  <span>{{
                    donation.isAnonymous
                      ? "Doação Anônima"
                      : donation.donationFrom.fullName
                  }}</span>
                </v-tooltip>

                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      icon
                      outlined
                      fab
                      size="16px"
                      class="gray icon-rounded ml-7"
                      @click="downloadReceipt(donation.receiptGuid)"
                      :disabled="donation.receiptGuid == null"
                    >
                      <v-icon color="error">fal fa-file-pdf</v-icon>
                    </v-btn>
                  </template>
                  <span>Recibo</span>
                </v-tooltip>

              </div>
            </v-list-item>
          </template>
        </v-list>
        <v-skeleton-loader
          ref="skeleton"
          :boilerplate="false"
          type="table-tfoot"
          :tile="false"
          class="mx-auto"
          v-show="loading"
        ></v-skeleton-loader>
        <v-card-actions v-show="!loading" class="justify-end pa-8">
          <v-layout style="max-width:150px" align-center>
            <v-flex grow pr-4
              >{{ firstDonationOfCurrentPage }}-{{
                lastDonationOfCurrentPage
              }}
              de {{ totalNumberOfDonations }}</v-flex
            >
            <v-flex>
              <v-select
                v-model="selectedPagination"
                :items="paginationOptions"
                outlined
                dense
                solo
                flat
                hide-details="auto"
                @change="loadDonations"
              ></v-select>
            </v-flex>
          </v-layout>
        </v-card-actions>
      </v-card>
      <div class="text-center mt-10 pa-5">
        <v-pagination
          v-show="numberOfPages > 0"
          v-model="currentPage"
          color="secondary"
          :length="numberOfPages"
          prev-icon="fal fa-chevron-left"
          next-icon="fal fa-chevron-right"
          @input="loadDonations"
          :disabled="loading"
        ></v-pagination>
      </div>
    </v-container>

    <!-- Modal -->
    <template>
      <v-dialog v-model="showCancelBilletDialog" persistent max-width="490">
        <v-card>
          <v-card-title>
            <h5 class="pt-5 pb-7 red--text">Cancelar boleto</h5>
          </v-card-title>
          <v-card-text>Deseja realmente cancelar seu boleto.</v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="px-6 py-5">
            <v-btn
              color="gray"
              rounded
              large
              depressed
              class="px-12"
              @click="showCancelBilletDialog = false"
              >Não</v-btn
            >
            <v-spacer></v-spacer>
            <v-btn
              color="red"
              rounded
              large
              depressed
              class="px-12"
              @click="cancelDonationBillet()"
              >Sim</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!-- Modal -->
    <template>
      <v-dialog
        v-model="showDonationBilletCancelledSuccessfullyMessage"
        persistent
        max-width="490"
      >
        <v-card>
          <v-card-title>
            <h5 class="pt-5 pb-7 red--text">Boleto cancelado com sucesso!</h5>
          </v-card-title>
          <v-card-text>Boleto cancelado com sucesso!</v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="px-6 py-5">
            <v-spacer></v-spacer>
            <v-btn
              color="gray"
              rounded
              large
              depressed
              class="px-12"
              @click="showDonationBilletCancelledSuccessfullyMessage = false"
              >Ok</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!-- Modal -->
    <template>
      <v-dialog
        v-model="showCannotCancelDonationBilletMessage"
        persistent
        max-width="490"
      >
        <v-card>
          <v-card-title>
            <h5 class="pt-5 pb-7 red--text">
              Você não pode cancelar esse boleto.
            </h5>
          </v-card-title>
          <v-card-text>Você não pode cancelar esse boleto.</v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="px-6 py-5">
            <v-spacer></v-spacer>
            <v-btn
              color="gray"
              rounded
              large
              depressed
              class="px-12"
              @click="showCannotCancelDonationBilletMessage = false"
              >Ok</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!-- Modal -->
    <template>
      <v-dialog v-model="showFilterDialog" persistent max-width="490">
        <v-card>
          <v-card-title>
            <h5 class="pt-5 pb-7">Filtrar</h5>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-select
                  clearable
                  v-model="selectedDonationStatus"
                  :items="donationStatusList"
                  :item-text="formatDonationStatus"
                  item-value="id"
                  label="Status de pagamento"
                  outlined
                  hide-details="auto"
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-select
                  clearable
                  v-model="selectedPaymentMethod"
                  :items="paymentMethodList"
                  item-text="name"
                  item-value="id"
                  label="Forma de pagamento"
                  outlined
                  hide-details="auto"
                ></v-select>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider class="lg"></v-divider>
          <v-card-actions class="px-6 py-5">
            <v-btn
              rounded
              outlined
              large
              depressed
              class="px-9 gray"
              @click="closeFilterDialog()"
              >Voltar</v-btn
            >
            <v-spacer></v-spacer>
            <v-btn
              color="secondary"
              rounded
              large
              depressed
              class="px-12"
              @click="closeFilterDialogAndLoadDonations()"
              >Filtrar</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import NavBar from "@/components/NavBar";
import HeaderPage from "@/components/HeaderPage";
import Routes from "@/router/names.js";
import SearchResult from "@/components/SearchResult";
import DonationOrderByOptions from "@/scripts/enums/orderByOptions.enum";
import DonationService from "@/scripts/services/donation.service";
import PaymentMethods from "@/scripts/enums/paymentMethods.enum";
import DonationStatus from "@/scripts/enums/donationStatus.enum";
import FormatterHelper from "@/scripts/helpers/formatter.helper";
export default {
  name: "DonationList",
  components: {
    NavBar,
    SearchResult,
    HeaderPage
  },
  data: () => ({
    donationService: new DonationService(),
    donationStatus: DonationStatus,
    paymentMethods: PaymentMethods,
    loadingCount: false,
    loadingList: false,
    totalNumberOfDonations: 0,
    currentPage: 1,
    selectedOrderBy: DonationOrderByOptions.Date,
    selectedPagination: 15,
    showCancelBilletDialog: false,
    showDonationBilletCancelledSuccessfullyMessage: false,
    showCannotCancelDonationBilletMessage: false,
    routes: Routes,
    isFiltered: false,
    showFilterDialog: false,
    selectedDonation: null,
    orderByOptions: [
      { id: DonationOrderByOptions.Date, name: "Ordenar por data" },
      // { id: DonationOrderByOptions.DueDate, name: "Ordenar por vencimento" },
      { id: DonationOrderByOptions.Value, name: "Ordenar por valor" },
      // { id: DonationOrderByOptions.Name, name: "Ordenar por nome" },
      { id: DonationOrderByOptions.Status, name: "Ordenar por status" },
      { id: DonationOrderByOptions.Type, name: "Ordenar por tipo" }
    ],
    paginationOptions: [15, 30, 50],
    donations: [],
    searchTerms: [],
    paymentMethodList: Array,
    donationStatusList: [
      { id: DonationStatus.Cancelled },
      { id: DonationStatus.Expired },
      { id: DonationStatus.Paid },
      { id: DonationStatus.Pending }
    ],
    selectedPaymentMethod: null,
    selectedDonationStatus: null,
    currentSearchTerm: "",
    searchTimeout: null,
    searchWaitTime: 2000,
    tour: null
  }),
  mounted() {
    this.loadDonations();
    this.donationService
      .listAllPaymentMethodsByLoggedAgent()
      .then(result => {
        result.push({id: 3, name: "DARF"})
        this.paymentMethodList = result
      });
    this.buildStepByStep();
  },
  computed: {
    loading() {
      return this.loadingList || this.loadingCount;
    },
    firstDonationOfCurrentPage() {
      return Math.min(
        (this.currentPage - 1) * this.selectedPagination + 1,
        this.totalNumberOfDonations
      );
    },
    lastDonationOfCurrentPage() {
      return Math.min(
        this.currentPage * this.selectedPagination,
        this.totalNumberOfDonations
      );
    },
    numberOfPages() {
      return Math.ceil(this.totalNumberOfDonations / this.selectedPagination);
    }
  },
  methods: {
    buildStepByStep() {
      let listTutorialAlreadyDone = localStorage.getItem('listTutorialDone');
      
      if (!listTutorialAlreadyDone || listTutorialAlreadyDone == false) {
        this.$nextTick(() => {
          this.tour = this.$shepherd({
            useModalOverlay: true,
            // classes: "", // classe para estilizar o componente
          });

          this.tour.addStep({
            attachTo: { element: this.$refs.header.getButton().$el, on: "top" },
            text: "Para realizar uma nova doação, clique aqui",
            buttons: [
              {
                text: "Fechar",
                action: this.tour.next
              }
            ]
          });

          this.tour.start();
        });

        localStorage.setItem('listTutorialDone', true);
      }
    },
    countDonationsByLoggedAgentAndFiltersCallback(result) {
      this.totalNumberOfDonations = result;
      this.loadingCount = false;
    },
    listDonationsByLoggedAgentCallback(result) {
      this.donations = result;
      this.loadingList = false;
    },
    getFilters() {
      return {
        orderBy: this.selectedOrderBy,
        currentPage: this.currentPage - 1,
        pageSize: this.selectedPagination,
        searchTerms: this.searchTerms,
        paymentMethod: this.selectedPaymentMethod,
        donationStatus: this.selectedDonationStatus
      };
    },
    loadDonations() {
      if (this.loading) return;
      this.loadingCount = true;
      this.loadingList = true;
      this.searchTimeout = null;
      this.donationService
        .countDonationsByLoggedAgentAndFilters(this.getFilters())
        .then(this.countDonationsByLoggedAgentAndFiltersCallback)
        .finally(() => (this.loadingCount = false));
      this.donationService
        .listDonationsByLoggedAgent(this.getFilters())
        .then(this.listDonationsByLoggedAgentCallback)
        .finally(() => (this.loadingList = false));
    },
    getClassBaseOnDonationStatus(status) {
      let className = "canceled";
      if (status == DonationStatus.Cancelled) className = "canceled";
      else if (status == DonationStatus.Expired) className = "expired";
      else if (status == DonationStatus.Paid) className = "paid";
      else if (status == DonationStatus.Pending) className = "pending";
      return `status-${className}`;
    },
    cancelDonationBillet() {
      let donationBilletId = this.selectedDonation.billetId;
      this.showCancelBilletDialog = false;
      this.donationService
        .cancelDonationBillet(donationBilletId)
        .then(this.cancelDonationBilletCallback)
        .catch(this.cancelDonationBilletCatch);
    },
    cancelDonationBilletCallback() {
      this.showDonationBilletCancelledSuccessfullyMessage = true;
      this.loadDonations();
    },
    cancelDonationBilletCatch() {
      this.showCannotCancelDonationBilletMessage = true;
    },
    downloadDonationBillet(billetId) {
      this.donationService
        .downloadDonationBillet(billetId)
        .then(this.downloadDonationBilletCallback);
    },
    downloadDonationBilletCallback(response) {
      if (response != null) {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Boleto.pdf");
        document.body.appendChild(link);
        link.click();
      }
    },
    formatDonationStatus(ds) {
      return FormatterHelper.formatDonationStatus(ds.id);
    },
    addSearchTerm(event) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(
          this.loadDonations,
          this.searchWaitTime
        );
      }
      if (event.key == "Enter") {
        if (
          this.currentSearchTerm &&
          this.searchTerms.indexOf(this.currentSearchTerm) == -1
        )
          this.searchTerms.push(this.currentSearchTerm);
        this.currentSearchTerm = "";
        if (!this.searchTimeout)
          this.searchTimeout = setTimeout(
            this.loadDonations,
            this.searchWaitTime
          );
      }
    },
    searchTermRemoved() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(this.loadDonations, this.searchWaitTime);
    },
    closeFilterDialog() {
      this.isFiltered = false;
      this.showFilterDialog = false;
    },
    closeFilterDialogAndLoadDonations() {
      this.closeFilterDialog();
      this.loadDonations();
    },
    downloadReceipt(receiptId){
      var url = process.env.VUE_APP_API_URL + "/Receipt/SignedPreview/?receiptId=" + receiptId;
        var link=document.createElement("a");
            link.id = 'receipt';
            link.href=url;
            link.download;
            link.click();
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.tour) this.tour.cancel();
    next();
  }
};
</script>
<style scoped>
@import "~shepherd.js/dist/css/shepherd.css";
</style>